import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Briefcase, CheckCircle, XCircle, Plus, X, Upload, FileArrowDown, Certificate,
  WhatsappLogo, EnvelopeSimple, ClockCounterClockwise, Warning,
} from "@phosphor-icons/react";

const STATUSES = [
  { id: "active", label: "Active" },
  { id: "under_review", label: "Under review" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function Placements() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null); // currently-open placement detail

  const refresh = async () => {
    const [pls, cs, en, cr] = await Promise.all([
      api.get("/placements"),
      api.get("/companies"),
      api.get("/registrations", { params: { status: "enrolled" } }),
      api.get("/courses", { params: { include_archived: true } }),
    ]);
    setItems(pls.data);
    setCompanies(cs.data);
    setEnrolled(en.data);
    setCourses(cr.data);
  };
  useEffect(() => { refresh(); }, []);

  // Map of course id → offers_placement so we can hide students enrolled in
  // completion-only courses from the placement creation flow.
  const courseAllowsPlacement = useMemo(() => {
    const map = {};
    for (const c of courses) map[c.id] = c.offers_placement !== false;
    return map;
  }, [courses]);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((p) => p.status === filter)),
    [items, filter]
  );
  const counts = useMemo(() => {
    const out = { all: items.length };
    STATUSES.forEach((s) => { out[s.id] = items.filter((p) => p.status === s.id).length; });
    return out;
  }, [items]);

  const isReviewer = me?.role === "admin" || me?.role === "manager";

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Workforce</div>
          <h1 className="font-display text-5xl font-black tracking-tighter">Placements.</h1>
          <p className="text-slate-500 mt-2">Track every student through their placement and certificate issuance.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="btn-create-placement" className="sms-btn-primary inline-flex items-center gap-2">
          <Plus size={16} weight="bold" /> New placement
        </button>
      </div>

      <div className="flex items-center gap-1 border border-slate-200 inline-flex mb-6" data-testid="placement-filters">
        {[{ id: "all", label: "All" }, ...STATUSES].map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            data-testid={`filter-${s.id}`}
            className={`px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${filter === s.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            {s.label} <span className="opacity-60">({counts[s.id] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="border border-slate-200" data-testid="placements-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="text-left px-4 py-3 font-medium">Student</th>
              <th className="text-left px-4 py-3 font-medium">Company</th>
              <th className="text-left px-4 py-3 font-medium">Period</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium"> </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No placements in this filter.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`placement-${p.id}`} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => setActive(p)}>
                <td className="px-4 py-3">
                  <div className="font-medium">{p.registration?.full_name || "—"}</div>
                  <div className="text-xs text-slate-500">{p.registration?.student_id || "—"} · {p.registration?.course_name || ""}</div>
                </td>
                <td className="px-4 py-3">{p.company?.name || "—"}{p.role_title && <div className="text-xs text-slate-500">{p.role_title}</div>}</td>
                <td className="px-4 py-3 font-mono-sms text-xs text-slate-500">
                  {p.start_date} → {p.end_date}
                  {p.overdue_review && <div className="text-[var(--sms-danger)] mt-0.5 inline-flex items-center gap-1"><Warning size={11} weight="bold" /> Period ended — request letter</div>}
                </td>
                <td className="px-4 py-3"><PlacementBadge status={p.status} /></td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)]">View →</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreatePlacementModal
          companies={companies.filter((c) => !c.archived)}
          enrolled={enrolled.filter((e) =>
            (!e.placement || ["approved", "rejected"].includes(e.placement?.status))
            && courseAllowsPlacement[e.course_id] !== false
          )}
          onClose={() => setCreating(false)}
          onSaved={async () => { setCreating(false); await refresh(); }}
        />
      )}

      {active && (
        <PlacementDetail
          placement={active}
          isReviewer={isReviewer}
          onClose={() => setActive(null)}
          onChanged={async () => { await refresh(); }}
        />
      )}
    </AdminLayout>
  );
}

function PlacementBadge({ status }) {
  const map = {
    active: { cls: "sms-badge-pending", txt: "Active" },
    under_review: { cls: "sms-badge-pending", txt: "Under review" },
    approved: { cls: "sms-badge-approved", txt: "Approved" },
    rejected: { cls: "sms-badge-rejected", txt: "Rejected" },
  };
  const m = map[status] || { cls: "sms-badge-pending", txt: status };
  return <span className={`sms-badge ${m.cls}`} data-testid={`status-${status}`}>{m.txt}</span>;
}

// -------------------------- Create placement modal --------------------------
function CreatePlacementModal({ companies, enrolled, onClose, onSaved }) {
  const [form, setForm] = useState({ registration_id: "", company_id: "", start_date: "", end_date: "", role_title: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/placements", form);
      toast.success("Placement created · student progress moved to 'placement'");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not create");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-lg p-6" data-testid="create-placement-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)]">New placement</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1">Assign a student</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4">
          <SelectField label="Student (enrolled)" value={form.registration_id} onChange={(v) => setForm({ ...form, registration_id: v })} testid="placement-student" required>
            <option value="">— select —</option>
            {enrolled.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.student_id} · {e.course_name}</option>)}
          </SelectField>
          <SelectField label="Partner company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testid="placement-company" required>
            <option value="">— select —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectField>
          <div className="grid grid-cols-2 gap-3">
            <FieldLine label="Start date *" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="placement-start" required />
            <FieldLine label="End date *" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="placement-end" required />
          </div>
          <FieldLine label="Role / position" value={form.role_title} onChange={(v) => setForm({ ...form, role_title: v })} placeholder="e.g. Junior Engineer" testid="placement-role" />
          <FieldLine label="Notes (optional)" multiline value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="placement-notes" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="sms-btn-secondary text-xs">Cancel</button>
          <button type="submit" disabled={busy} data-testid="placement-save" className="sms-btn-primary">{busy ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}

// -------------------------- Detail / completion / review --------------------------
function PlacementDetail({ placement, isReviewer, onClose, onChanged }) {
  const [p, setP] = useState(placement);
  const [busy, setBusy] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const fileInputId = `letter-${p.id}`;
  const refreshSelf = async () => {
    const r = await api.get("/placements", { params: { registration_id: p.registration_id } });
    const fresh = r.data.find((x) => x.id === p.id);
    if (fresh) setP(fresh);
    await onChanged();
  };

  const onLetter = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Letter must be under 8 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api.post(`/placements/${p.id}/submit-completion`, {
          letter_base64: reader.result,
          letter_filename: file.name,
        });
        toast.success("Completion letter uploaded · awaiting review");
        await refreshSelf();
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
      } finally {
        setBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const review = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/placements/${p.id}/review`, { approve, review_notes: reviewNotes });
      setP(data);
      toast.success(approve ? "Approved · certificate issued" : "Rejected — student moved back to 'studying'");
      setReviewNotes("");
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Review failed");
    } finally {
      setBusy(false);
    }
  };

  const certUrl = p.certificate_token ? `${BACKEND_URL}/api/placements/cert/${p.certificate_token}/pdf` : null;
  const phone = (p.registration?.phone || "").replace(/[^0-9]/g, "");
  const studentEmail = ""; // we don't capture email today
  const message = certUrl
    ? `Hi ${p.registration?.full_name?.split(" ")[0] || ""}, congratulations on completing your placement at ${p.company?.name || "your placement"}! Your certificate: ${certUrl}`
    : "";
  const waUrl = phone && certUrl ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : "";
  const mailtoUrl = certUrl ? `mailto:${studentEmail}?subject=${encodeURIComponent("Your Placement Certificate")}&body=${encodeURIComponent(message)}` : "";
  const letterUrl = p.has_completion_letter ? `${BACKEND_URL}/api/placements/${p.id}/completion-letter` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" data-testid="placement-detail">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] inline-flex items-center gap-2"><Briefcase size={12} weight="duotone" /> Placement</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1">{p.registration?.full_name}</div>
            <div className="text-xs text-slate-500 mt-1">
              {p.registration?.student_id} · {p.registration?.course_name}
            </div>
          </div>
          <button onClick={onClose}><X size={18} weight="bold" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <Stat label="Company" value={p.company?.name || "—"} />
          <Stat label="Role" value={p.role_title || "—"} />
          <Stat label="Period" value={`${p.start_date} → ${p.end_date}`} />
          <Stat label="Status" value={<PlacementBadge status={p.status} />} />
        </div>

        {p.overdue_review && p.status === "active" && (
          <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs mb-4 inline-flex items-center gap-2" data-testid="placement-overdue-banner">
            <ClockCounterClockwise size={14} weight="duotone" /> Placement period has ended — request the completion letter from the student.
          </div>
        )}

        {/* Completion letter */}
        <div className="border border-slate-200 p-4 mb-4">
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">Completion letter</div>
          {p.has_completion_letter ? (
            <div className="flex flex-wrap items-center gap-2">
              <a href={letterUrl} target="_blank" rel="noopener noreferrer" data-testid="view-letter" className="sms-btn-secondary text-xs inline-flex items-center gap-1">
                <FileArrowDown size={12} weight="bold" /> View letter
              </a>
              <span className="font-mono-sms text-[10px] text-slate-500">
                Uploaded by {p.completion_uploaded_by} on {new Date(p.completion_uploaded_at).toLocaleString()}
              </span>
            </div>
          ) : (
            <div className="text-xs text-slate-500">No letter uploaded yet.</div>
          )}
          {(p.status === "active" || p.status === "under_review" || p.status === "rejected") && (
            <div className="mt-3">
              <input type="file" accept="application/pdf,image/*" id={fileInputId} className="hidden" data-testid="upload-letter" onChange={(e) => onLetter(e.target.files?.[0])} />
              <label htmlFor={fileInputId} className="sms-btn-primary text-xs inline-flex items-center gap-2 cursor-pointer">
                <Upload size={12} weight="bold" /> {p.has_completion_letter ? "Replace letter" : "Upload completion letter"}
              </label>
            </div>
          )}
        </div>

        {/* Review (under_review only) */}
        {p.status === "under_review" && isReviewer && (
          <div className="border border-slate-200 p-4 mb-4 bg-slate-50">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">Review</div>
            <textarea rows={2} className="sms-input mb-3" placeholder="Optional review notes" value={reviewNotes} data-testid="review-notes" onChange={(e) => setReviewNotes(e.target.value)} />
            <div className="flex items-center gap-2">
              <button onClick={() => review(true)} disabled={busy} data-testid="btn-approve-placement" className="sms-btn-primary inline-flex items-center gap-2">
                <CheckCircle size={14} weight="bold" /> Approve & issue certificate
              </button>
              <button onClick={() => review(false)} disabled={busy} data-testid="btn-reject-placement" className="sms-btn-secondary inline-flex items-center gap-2 text-[var(--sms-danger)]">
                <XCircle size={14} weight="bold" /> Reject
              </button>
            </div>
          </div>
        )}

        {/* Reviewed footer */}
        {(p.status === "approved" || p.status === "rejected") && (
          <div className="text-xs text-slate-500 mb-4">
            {p.status === "approved" ? "Approved" : "Rejected"} by {p.reviewed_by} on {new Date(p.reviewed_at).toLocaleString()}
            {p.review_notes && <div className="italic mt-1">"{p.review_notes}"</div>}
          </div>
        )}

        {/* Certificate share */}
        {p.status === "approved" && certUrl && (
          <div className="border border-[var(--sms-primary)]/30 bg-[var(--sms-primary)]/5 p-4" data-testid="certificate-share">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] inline-flex items-center gap-2 mb-3">
              <Certificate size={12} weight="duotone" /> Certificate issued
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a href={certUrl} target="_blank" rel="noopener noreferrer" data-testid="view-certificate" className="sms-btn-primary text-xs inline-flex items-center gap-1">
                <FileArrowDown size={12} weight="bold" /> View certificate
              </a>
              {phone && (
                <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="sms-btn-secondary text-xs inline-flex items-center gap-1 bg-green-50 border-green-200 text-green-800 hover:bg-green-100">
                  <WhatsappLogo size={12} weight="duotone" /> WhatsApp
                </a>
              )}
              <a href={mailtoUrl} data-testid="share-email" className="sms-btn-secondary text-xs inline-flex items-center gap-1">
                <EnvelopeSimple size={12} weight="duotone" /> Email
              </a>
            </div>
          </div>
        )}

        {/* Draft preview button (for any logged-in user before approval) */}
        {p.status !== "approved" && (
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              const t = localStorage.getItem(TOKEN_KEY);
              const r = await fetch(`${BACKEND_URL}/api/placements/${p.id}/certificate.pdf`, { headers: { Authorization: `Bearer ${t}` } });
              if (!r.ok) { toast.error("Preview failed"); return; }
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank", "noopener,noreferrer");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }}
            data-testid="preview-certificate"
            className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[var(--sms-primary)] inline-flex items-center gap-1"
          >
            <Certificate size={12} weight="duotone" /> Preview certificate (DRAFT)
          </a>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function FieldLine({ label, value, onChange, type = "text", placeholder, testid, required, multiline }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      {multiline ? (
        <textarea rows={2} className="sms-input" value={value} placeholder={placeholder} required={required} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className="sms-input" value={value} placeholder={placeholder} required={required} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, children, testid, required }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <select className="sms-input" value={value} required={required} data-testid={testid} onChange={(e) => onChange(e.target.value)}>{children}</select>
    </div>
  );
}
